import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import BeforeFirstMileTable from '../components/BeforeFirstMileTable/BeforeFirstMileTable';
import Box from '@components/Box';
import cn from 'classnames';
import DataTable, { BeforeProps, DataViewHandle } from '@containers/DataTable';
import DisposalProcessingModalWindow from '../modals/DisposalProcessingModalWindow';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import PaymentCompletedModalWindow from '@components/modals/PaymentCompletedModalWindow';
import PaymentModalWindow from '@components/modals/PaymentModalWindow';
import PlacingOrderModalWindow from '@components/modals/PlacingOrderModalWindow';
import ProgressBar from '@components/ProgressBar';
import Reason from '../components/Reason';
import TableSelectionPanel from '@components/Table/TableSelectionPanel';
import TrackingNumber from "@components/TrackingNumber";
import { actionAddresses } from '@utils/handlerSubmitActions';
import { AddSellerPanel } from '@components/AddSeller';
import { Button, ButtonSize, ButtonType } from '@components/Button';
import {
  checkBalanceReplenishment,
  checkBalanceReplenishmentResponse,
  getPaymentLink,
  getWallet,
  sendTopUpBalance,
  sendTopUpBalanceResponse,
  TGetPaymentLinkResponse
} from '@api/apiClient/finance';
import { Column, Row } from '@components/Grid';
import { createSumFromObject } from '@utils/formatValues';
import {
  EPlacingOrder,
  TPlacingOrderData,
  TPlacingOrderDataItem
} from '@components/modals/PlacingOrderModalWindow/types';
import { EPlacingOrderViewParcel, getPlacingOrderView } from '@utils/createPlacingOrderView';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { getCurrentLanguage } from '@utils/lang';
import { getDaysBetweenDates, getProgressFromTo } from '@utils/progress';
import {
  getProblematicFirstMileParcels,
  sendFirstMileReturnOrder,
  TProblematicFirstMileParcel,
  TReturnOrderAddress,
  TReturnOrderParams
} from '@api/apiClient/problematic';
import { IFirstMileTabProps } from '../types';
import { Modal } from '@components/Modal';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize, TextType } from '@components/Text';
import { TPlacingOrderListItem } from '@components/PlacingOrderView/types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';

const FirstMileTab: React.FC<IFirstMileTabProps> = (props: IFirstMileTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    onUpdateCustoms,
    onUpdateDisposal,
    onUpdateFirstMile,
    onUpdateOnWay,
    sellersNotFound,
    setIsFilterOpen
  } = props;

  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenDisposalProcessingWindow, setIsOpenDisposalProcessingWindow] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [isOpenPaymentCompletedWindow, setIsOpenPaymentCompletedWindow] = useState<boolean>(false);
  const [isOpenPaymentWindow, setIsOpenPaymentWindow] = useState<boolean>(false);
  const [isOpenReturnProcessingWindow, setIsOpenReturnProcessingWindow] = useState<boolean>(false);
  const [parcelId, setParcelId] = useState<string>('');
  const [parcelsGoodView, setParcelsGoodView] = useState<TPlacingOrderListItem[] | undefined>(undefined);
  const [paymentError, setPaymentError] = useState<string | undefined>(undefined);
  const [paymentInProcessed, setPaymentInProcessed] = useState<boolean>(false);
  const [selectedDelivery, setSelectedDelivery] = useState<number>(0);
  const [selectedParcels, setSelectedParcels] = useState<Map<string, TProblematicFirstMileParcel>>(new Map());
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [selectedSumRub, setSelectedSumRub] = useState<number>(0);
  const [submitParams, setSubmitParams] = useState<TReturnOrderParams | undefined>(undefined);

  const dataRef = useRef<DataViewHandle>(null);
  const timeoutCheckBalance = useRef<number | null>(null);
  const timeoutCheckPayment = useRef<number | null>(null);

  const currentLanguage = getCurrentLanguage();
  const navigate = useNavigate();
  const { account } = useProfile();
  const { t } = useTranslation();

  const handleSelectionChange = useCallback((
      selectedIds: string[],
      items: TProblematicFirstMileParcel[]
  ) => {
    setSelectedRowIds(selectedIds);

    const parcels: Map<string, TProblematicFirstMileParcel> = new Map();

    selectedParcels.forEach((parcel: TProblematicFirstMileParcel, key: string) => {
      const item = items.find((v: TProblematicFirstMileParcel) => v.id === key);
      if (!item) parcels.set(key, parcel);
    });

    selectedIds.forEach((id) => {
      const item = items.find((v: TProblematicFirstMileParcel) => (v.id === id));
      if (item) {
        parcels.set(id, item);
      } else {
        const selectedItem = selectedParcels.get(id);
        if (selectedItem) {
          parcels.set(id, selectedItem);
        }
      }
    });

    const parcelsArray: TProblematicFirstMileParcel[] = Array.from(parcels.values());

    const parcelsGoodView: TPlacingOrderListItem[] =
        getPlacingOrderView(parcelsArray, EPlacingOrderViewParcel.PROBLEMATIC_FIRST_MILE_RETURN);

    const delivery = 0;
    const sumRub = +parcelsArray.reduce((acc, v) => (acc + (v.declaredValue?.amount ?? 0)), 0).toFixed(2);

    setParcelsGoodView(parcelsGoodView);
    setSelectedDelivery(delivery);
    setSelectedParcels(parcels);
    setSelectedSumRub(sumRub);
  }, [selectedParcels]);

  const onUpdateData = () => {
    if (dataRef.current?.refresh) {
      dataRef.current.refresh();
    }
    onUpdateCustoms();
    onUpdateDisposal();
    onUpdateFirstMile();
    onUpdateOnWay();
    setSelectedParcels(new Map());
    setSelectedRowIds([]);
  };

  const { mutate: mutateCheckBalanceReplenishment } = useMutation({
    mutationFn: checkBalanceReplenishment
  });

  const { mutate: mutateCheckBalance } = useMutation({
    mutationFn: getWallet,
    onSuccess: (result) => {
      if (result.balance >= selectedDelivery) {
        mutateSendFirstMileReturnOrder(submitParams!);
      } else {
        waitingBalanceReplenished();
      }
    }
  });

  const {
    data: dataSendTopUpBalance,
    error: errorSendTopUpBalance,
    isError: isErrorSendTopUpBalance,
    isLoading: isLoadingSendTopUpBalance,
    mutate: mutateSendTopUpBalance,
    reset: resetSendTopUpBalance
  } = useMutation({
    mutationFn: sendTopUpBalance,
    onSuccess: (result: sendTopUpBalanceResponse) => {
      setIsOpenPaymentWindow(true);
      setIsOpenReturnProcessingWindow(false);
      setPaymentTimeout();
      waitingBalanceReplenished(result.outTradeNo);
    }
  });

  const {
    error: errorGetPaymentLink,
    isError: isErrorGetPaymentLink,
    isLoading: isLoadingGetPaymentLink,
    mutate: mutateGetPaymentLink
  } = useMutation({
    mutationFn: getPaymentLink,
    onSuccess: (result: TGetPaymentLinkResponse) => {
      setPaymentInProcessed(true);
      setPaymentTimeout();
      waitingBalanceReplenished();
      window.open(result.paymentLink);
    }
  });

  const {
    error: errorSendFirstMileReturnOrder,
    isError: isErrorSendFirstMileReturnOrder,
    isLoading: isLoadingSendFirstMileReturnOrder,
    mutate: mutateSendFirstMileReturnOrder,
    reset: resetSendFirstMileReturnOrder
  } = useMutation({
    mutationFn: sendFirstMileReturnOrder,
    onSuccess: () => {
      onCloseReturnProcessing();
      onUpdateData();
      setIsOpenPaymentCompletedWindow(true);
    }
  });

  const waitingBalanceReplenished = (id?: string): void => {
    timeoutCheckBalance.current = window.setTimeout(() => {
      if (id) {
        mutateCheckBalanceReplenishment(id, {
          onSuccess: (data: checkBalanceReplenishmentResponse) => {
            if (data.isProcess) {
              if (!paymentInProcessed) setPaymentInProcessed(true);
              waitingBalanceReplenished(id);
            } else {
              if (paymentInProcessed) setPaymentInProcessed(false);
              if (data.isSuccess) {
                clearTimeout(timeoutCheckPayment.current as number);
                mutateCheckBalance();
              } else {
                if (data.errorCode) {
                  clearTimeout(timeoutCheckPayment.current as number);
                  setPaymentError(data.errorCode);
                } else {
                  waitingBalanceReplenished(id);
                }
              }
            }
          }
        });
      } else {
        mutateCheckBalance();
      }
    }, 3000);
  };

  const setPaymentTimeout = (): void => {
    timeoutCheckPayment.current = window.setTimeout(() => {
      clearTimeout(timeoutCheckBalance.current as number);
      setPaymentInProcessed(false);
    }, 600000);
  };

  const onClosePaymentReturn = (): void => {
    clearTimeout(timeoutCheckBalance.current as number);
    clearTimeout(timeoutCheckPayment.current as number);
    setIsOpenPaymentWindow(false);
    setPaymentInProcessed(false);
  };

  const onClickPaymentLink = (amount: string): void => {
    mutateGetPaymentLink(amount);
  };

  const onCloseParcel = (): void => {
    setIsOpenParcelWindow(false);
  };

  const onCloseDisposalProcessing = (): void => {
    setIsOpenDisposalProcessingWindow(false);
  };

  const onCloseReturnProcessing = (): void => {
    resetSendFirstMileReturnOrder();
    resetSendTopUpBalance();
    setIsOpenReturnProcessingWindow(false);
    setPaymentInProcessed(false);
  };

  const handlerSubmit = (forms: TPlacingOrderData): void => {
    const addresses: TReturnOrderAddress[] = forms.map((item: TPlacingOrderDataItem) => ({
      address: item.address,
      contactInfo: {
        name: item.fullName,
        phone: item.phone
      }
    }));
    const params: TReturnOrderParams = {
      addresses: addresses,
      parcelIds: parcelsGoodView!.map((item: TPlacingOrderListItem) => item.id)
    };
    if (account!.balance >= selectedDelivery) {
      mutateSendFirstMileReturnOrder(params);
    } else {
      setSubmitParams(params);
      if (account!.region === 'TR') {
        onCloseReturnProcessing();
        setIsOpenPaymentWindow(true);
      } else {
        mutateSendTopUpBalance({
          amount: Math.round((selectedDelivery - account!.balance) * 100) / 100
        });
      }
    }
    actionAddresses(forms);
  };

  const BeforeFirstMileTableComponent = useCallback((props: BeforeProps<TProblematicFirstMileParcel>) => {
    return <BeforeFirstMileTable {...props} />;
  }, []);

  const columns: TableColumn<TProblematicFirstMileParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TProblematicFirstMileParcel>) => (
        <TrackingNumber
          externalId={row.data?.externalId}
          trackNumber={row.data?.trackNumber}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcelId(row.data?.id);
          }}
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.reason'),
      accessor: (row: TableRowType<TProblematicFirstMileParcel>) => (
          <div className={styles.reason}>
            <Reason value={row.data.reason}/>
          </div>
      )
    },
    {
      header: t('problematicParcelsPage.service'),
      accessor: (row: TableRowType<TProblematicFirstMileParcel>) => row.data.serviceName,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.price'),
      accessor: (row: TableRowType<TProblematicFirstMileParcel>) => (
          <div className={styles.price}>
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
            >
              {createSumFromObject(row.data.declaredValue)}
            </Text>
            {/*<Text*/}
            {/*    color={TextColor.DARK_MUTED}*/}
            {/*    size={TextSize.XS}*/}
            {/*>*/}
            {/*  {createSumFromObject(row.data.declaredValue)}*/}
            {/*</Text>*/}
          </div>
      ),
      alignment: 'right',
      width: '120px'
    },
    {
      header: t('problematicParcelsPage.sum'),
      accessor: () => (
          <div className={styles.actionPrice}>
            {createSumFromObject({ amount: 0, currency: 'CNY' })}
          </div>
      ),
      fixWidth: true,
      noOverflow: true,
      type: TableColumnType.PRIMARY,
      width: '100px'
    },
    {
      header: t('problematicParcelsPage.seller'),
      accessor: (row: TableRowType<TProblematicFirstMileParcel>) => (
          <AvatarWithMarketplace
              id={row.data.sellerId}
              name={row.data.sellerName}
              marketplace={row.data.marketplaceType}
          />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t('problematicParcelsPage.toRecycle'),
      accessor: (row: TableRowType<TProblematicFirstMileParcel>) => {
        const restDays = getDaysBetweenDates(null, row.data.estimatedUtilizationDate);
        return (
            <ProgressBar
                colors={[
                  { value: 300 / 7, main: '#1e3ebc', bg: '#e8ebf8', label: '#1e3ebc' },
                  { value: 0, main: '#ee6373', bg: '#fce0e3', label: '#ee6373' }
                ]}
                column
                label={restDays ? `${restDays} ${t('problematicParcelsPage.days')}` : t('problematicParcelsPage.lastDay')}
                progress={getProgressFromTo(row.data.createAt, row.data.estimatedUtilizationDate, true)}
            />
        );
      },
      width: '100px'
    }
  ], [currentLanguage]);

  return (
      <div>
        {sellersNotFound ? (
            <AddSellerPanel
                isFirstSeller
                onSuccess={onUpdateData}
            />
        ) : (
            <>
              <Box pb={5}>
                <div>
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      type={TextType.BLOCK}
                  >
                    {t('problematicParcelsPage.firstMile.description-1')}
                  </Text>
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      type={TextType.BLOCK}
                  >
                    {t('problematicParcelsPage.firstMile.description-2')}
                  </Text>
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      type={TextType.BLOCK}
                  >
                    {t('problematicParcelsPage.firstMile.description-3')}
                  </Text>
                </div>
              </Box>
              <Box
                  pb={5}
                  className={cn(!isFilterOpen && styles.hidden)}
              >
                <Column phone={12}>
                  <Row
                      justify="right"
                      gap={5}
                  >
                    <Filters
                        id="problematic-firstMile"
                        openFilters={() => setIsFilterOpen(true)}
                        items={[
                          {
                            name: 'trackOrExternalNumbers',
                            type: FilterTypes.MULTI_SEARCH,
                            placeholder: t('filters.searchByTrackNumber')
                          },
                          {
                            name: 'serviceIds',
                            type: FilterTypes.SERVICE_PROBLEMATIC
                          },
                          {
                            name: 'sellerIds',
                            type: FilterTypes.SELLER
                          },
                          {
                            name: 'marketplaceType',
                            type: FilterTypes.MARKETPLACE
                          }
                        ]}
                        onChange={(v, isReady) => {
                          if (isReady) {
                            setIsInitialized(true);
                          }
                          setInnerFilters(v);
                        }}
                        values={innerFilters}
                        asPanel
                    />
                  </Row>
                </Column>
              </Box>
              <TableSelectionPanel
                  isShown={selectedRowIds.length > 0}
                  items={[
                    {
                      label: t('problematicParcelsPage.selected'),
                      value: selectedRowIds.length
                    },
                    {
                      label: t('problematicParcelsPage.forTheAmount'),
                      value: createSumFromObject({ amount: selectedSumRub, currency: 'RUB' })
                    }
                  ]}
                  buttons={[
                    (<Button
                        key="dispose"
                        onClick={() => setIsOpenDisposalProcessingWindow(true)}
                        size={ButtonSize.S}
                        type={ButtonType.RED_SKELETON}
                        title={t('problematicParcelsPage.dispose')}
                    />),
                    (<Button
                        key="returnFor"
                        onClick={() => setIsOpenReturnProcessingWindow(true)}
                        size={ButtonSize.S}
                        type={ButtonType.PRIMARY}
                        title={t('problematicParcelsPage.return')}
                    />)
                  ]}
              >
                <DataTable<TProblematicFirstMileParcel>
                    Before={BeforeFirstMileTableComponent}
                    columns={columns}
                    dataRef={dataRef}
                    defaultPageSize={10}
                    filters={innerFilters}
                    getData={getProblematicFirstMileParcels}
                    notFound={t('problematicParcelsPage.noParcels')}
                    onSelectionChange={handleSelectionChange}
                    pending={!isInitialized}
                    queryKey="getProblematicFirstMileParcels"
                    selectable
                    selectedIds={selectedRowIds}
                />
              </TableSelectionPanel>
              <Modal
                  bodyWithoutIndentations
                  isOpen={isOpenParcelWindow}
                  onClickOverlay={onCloseParcel}
                  onClose={onCloseParcel}
                  width={900}
              >
                <ParcelModalWindow parcelId={parcelId} isProblematic />
              </Modal>
              <Modal
                  isOpen={isOpenDisposalProcessingWindow}
                  onClickOverlay={onCloseDisposalProcessing}
                  onClose={onCloseDisposalProcessing}
                  width={650}
              >
                <DisposalProcessingModalWindow
                    handlerOnCloseDisposalProcessing={onCloseDisposalProcessing}
                    handlerRefreshData={onUpdateData}
                    selectedRowIds={selectedRowIds}
                />
              </Modal>
              <Modal
                  isOpen={isOpenReturnProcessingWindow}
                  onClickOverlay={onCloseReturnProcessing}
                  onClose={onCloseReturnProcessing}
                  width={650}
              >
                <PlacingOrderModalWindow
                    costOfService={selectedDelivery}
                    errorSendPlacingOrder={errorSendFirstMileReturnOrder}
                    errorSendTopUpBalance={errorSendTopUpBalance}
                    handlerOnClosePlacingOrderProcessing={onCloseReturnProcessing}
                    handlerResetSendOrder={resetSendFirstMileReturnOrder}
                    handlerSubmit={handlerSubmit}
                    isErrorSendPlacingOrder={isErrorSendFirstMileReturnOrder}
                    isErrorSendTopUpBalance={isErrorSendTopUpBalance}
                    isLoadingSendPlacingOrder={isLoadingSendFirstMileReturnOrder}
                    isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
                    parcelsGood={parcelsGoodView}
                    placingOrderType={EPlacingOrder.PROBLEMATIC_FIRST_MILE}
                />
              </Modal>
              <Modal
                  isOpen={isOpenPaymentWindow}
                  onClickOverlay={onClosePaymentReturn}
                  onClose={onClosePaymentReturn}
                  width={560}
              >
                <PaymentModalWindow
                    dataSendTopUpBalance={dataSendTopUpBalance!}
                    errorGetPaymentLink={errorGetPaymentLink}
                    handlerOnClickPaymentLink={onClickPaymentLink}
                    isErrorGetPaymentLink={isErrorGetPaymentLink}
                    isLoadingGetPaymentLink={isLoadingGetPaymentLink}
                    isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
                    mutateSendTopUpBalance={mutateSendTopUpBalance}
                    paymentError={paymentError}
                    paymentInProcessed={paymentInProcessed}
                    selectedSum={selectedDelivery}
                    subTitle={t('paymentModalWindow.subTitle_1')}
                />
              </Modal>
              <Modal
                  isOpen={isOpenPaymentCompletedWindow}
                  onClickOverlay={() => setIsOpenPaymentCompletedWindow(false)}
                  onClose={() => setIsOpenPaymentCompletedWindow(false)}
                  width={560}
              >
                <PaymentCompletedModalWindow
                    action={() => navigate('/problematic-parcels?tab=OnWayTab')}
                    btnText={t('problematicParcelsPage.tabReturns')}
                    handlerSetIsOpenPaymentCompletedWindow={setIsOpenPaymentCompletedWindow}
                    subTitle={t('problematicParcelsPage.paymentCompletedModalWindow.subTitle', { tab: t('problematicParcelsPage.tabReturns') })}
                    title={t('paymentCompletedModalWindow.info_1')}
                />
              </Modal>
            </>
        )}
      </div>
  );
};

export default FirstMileTab;
